import React, { useEffect, useState } from "react";
import {EditButton, List, ShowButton, useDataGrid, CreateButton} from "@refinedev/mui";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import {IResourceComponentsProps, useTranslate, useNavigation} from "@refinedev/core";
import { useAuth } from "../../contexts/auth-context";
import { canModifyResource, UserRole } from "../../utils/role-permissions";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Alert,
    useTheme,
    alpha,
    CircularProgress,
    Stack,
    Paper,
    Chip,
    Avatar,
    Tooltip,
    IconButton,
    TextField,
    InputAdornment
} from "@mui/material";
import {
    Business,
    Add,
    People,
    Edit,
    Visibility,
    Search,
    Domain,
    GroupWork,
    BusinessCenter
} from "@mui/icons-material";
import { Building2, Users, Shield } from "lucide-react";
import axiosInstance from "../../api/axios-config";
import { LoadingSkeleton } from "../../components/common/LoadingSkeleton";
import { EmptyState } from "../../components/common/EmptyState";
import { SectionCard } from "../../components/common/SectionCard";
import { StatusBadge } from "../../components/common/StatusBadge";

export const CompanyList: React.FC<IResourceComponentsProps> = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const { user: currentUser } = useAuth();
    const { create } = useNavigation();
    const { dataGridProps } = useDataGrid();
    const [loading, setLoading] = useState(true);
    const [hasCompany, setHasCompany] = useState(false);
    const [searchText, setSearchText] = useState("");

    // Check if user can modify resources
    const canModify = currentUser?.role ? canModifyResource(currentUser.role as UserRole, 'company') : false;
    const isGuest = currentUser?.role === 'GUEST';

    useEffect(() => {
        checkUserCompany();
    }, [currentUser]);

    const checkUserCompany = async () => {
        if (!currentUser) return;

        setLoading(true);
        try {
            const response = await axiosInstance.get('/company');

            // If response is an object with company data (not an array), user has a company
            if (response.data && !Array.isArray(response.data)) {
                setHasCompany(true);
            } else if (Array.isArray(response.data) && response.data.length > 0) {
                setHasCompany(true);
            } else {
                setHasCompany(false);
            }
        } catch (error) {
            console.error('Error checking company:', error);
            setHasCompany(false);
        } finally {
            setLoading(false);
        }
    };

    const filteredData = React.useMemo(() => {
        if (!dataGridProps.rows) return [];

        return dataGridProps.rows.filter((row: any) => {
            if (!searchText) return true;

            const searchLower = searchText.toLowerCase();
            return (
                row.nom?.toLowerCase().includes(searchLower) ||
                row.id?.toString().includes(searchLower)
            );
        });
    }, [dataGridProps.rows, searchText]);

    const columns = React.useMemo<GridColDef[]>(
        () => [
            {
                field: "id",
                headerName: translate("company.fields.id"),
                type: "number",
                width: 80,
                renderCell: ({ value }) => (
                    <Chip
                        label={`#${value}`}
                        size="small"
                        variant="outlined"
                        sx={{
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            fontWeight: 500
                        }}
                    />
                ),
            },
            {
                field: "nom",
                flex: 1,
                headerName: translate("company.fields.name"),
                minWidth: 250,
                renderCell: ({ row }) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                            sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                                width: 40,
                                height: 40
                            }}
                        >
                            <Building2 size={20} />
                        </Avatar>
                        <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {row.nom}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                ID: {row.id}
                            </Typography>
                        </Box>
                    </Box>
                ),
            },
            {
                field: "limitUser",
                headerName: translate("company.fields.limitUser"),
                width: 150,
                renderCell: ({ row }) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StatusBadge
                            label={`${row.limitUser || 0}`}
                            color="info"
                            status="info"
                            icon={<Users size={16} />}
                            variant="filled"
                        />
                    </Box>
                ),
            },
            {
                field: "count",
                headerName: translate("company.fields.userCount", "Utilisateurs"),
                width: 150,
                renderCell: ({ row }) => {
                    const percentage = row.limitUser ? (row.count / row.limitUser) * 100 : 0;
                    const isNearLimit = percentage >= 80;

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {row.count || 0} / {row.limitUser || '∞'}
                                    </Typography>
                                    {row.limitUser && (
                                        <Typography
                                            variant="caption"
                                            color={isNearLimit ? "error" : "text.secondary"}
                                        >

                                        </Typography>
                                    )}
                                </Box>
                                {row.limitUser && (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: 4,
                                            bgcolor: alpha(theme.palette.divider, 0.2),
                                            borderRadius: 2,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: `${Math.min(percentage, 100)}%`,
                                                height: '100%',
                                                bgcolor: isNearLimit ? theme.palette.error.main : theme.palette.success.main,
                                                transition: 'width 0.3s ease'
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                },
            },
            {
                field: "status",
                headerName: translate("company.fields.status", "Statut"),
                width: 120,
                renderCell: ({ row }) => {
                    const isActive = row.count > 0;
                    return (
                        <StatusBadge
                            status={isActive ? "active" : "inactive"}
                            label={isActive ? translate("company.active", "Active") : translate("company.inactive", "Inactive")}
                            color={isActive ? "success" : "default"}
                            icon={isActive ? <Shield size={16} /> : undefined}
                        />
                    );
                },
            },
            {
                field: "actions",
                headerName: translate("table.actions"),
                sortable: false,
                width: 120,
                renderCell: function render({ row }) {
                    return (
                        <Stack direction="row" spacing={0.5}>
                            {canModify && currentUser?.role !== 'GUEST' && (
                                // MODERATOR can only edit their own company
                                (currentUser?.role === 'ADMIN' ||
                                 (currentUser?.role === 'MODERATOR' && currentUser?.entrepriseId === row.id)) && (
                                    <Tooltip title={translate("buttons.edit")}>
                                        <IconButton size="small" color="primary">
                                            <EditButton hideText recordItemId={row.id}>
                                                <Edit fontSize="small" />
                                            </EditButton>
                                        </IconButton>
                                    </Tooltip>
                                )
                            )}
                            <Tooltip title={translate("buttons.show")}>
                                <IconButton size="small">
                                    <ShowButton hideText recordItemId={row.id}>
                                        <Visibility fontSize="small" />
                                    </ShowButton>
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    );
                },
                align: "center",
                headerAlign: "center",
            },
        ],
        [translate, canModify, currentUser, theme],
    );

    // Show loading state
    if (loading) {
        return <LoadingSkeleton />;
    }

    // Special case for GUEST users without company
    if (isGuest && !hasCompany) {
        return (
            <Box sx={{ p: 3 }}>
                <Card
                    sx={{
                        maxWidth: 600,
                        mx: 'auto',
                        textAlign: 'center',
                        background: theme.palette.mode === 'dark'
                            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`
                            : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${theme.palette.background.paper} 100%)`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        boxShadow: theme.shadows[4],
                    }}
                >
                    <CardContent sx={{ py: 6 }}>
                        <Box
                            sx={{
                                width: 120,
                                height: 120,
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto',
                                mb: 3,
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: -2,
                                    borderRadius: '50%',
                                    padding: 2,
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                                    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                    maskComposite: 'exclude',
                                    opacity: 0.5
                                }
                            }}
                        >
                            <BusinessCenter sx={{ fontSize: 60, color: theme.palette.primary.main }} />
                        </Box>

                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                            {translate('company.no_company_title', 'Créez votre organisation')}
                        </Typography>

                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
                            {translate('company.no_company_desc', 'Pour commencer à inviter vos proches ou collaborateurs, vous devez d\'abord créer votre organisation.')}
                        </Typography>

                        <Stack direction="row" spacing={2} justifyContent="center">
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<Add />}
                                onClick={() => create('company')}
                                sx={{
                                    py: 1.5,
                                    px: 4,
                                    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                                    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                                    '&:hover': {
                                        background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                                    }
                                }}
                            >
                                {translate('company.create_button', 'Créer mon organisation')}
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    // Determine if the current user can create a company
    const canCreateCompany = canModify && currentUser?.role !== 'GUEST' &&
        (currentUser?.role === 'ADMIN' || (currentUser?.role === 'MODERATOR' && !currentUser?.entrepriseId));

    return (
        <List
            canCreate={canCreateCompany}
            headerButtons={({ defaultButtons }) => {
                // Return only the default buttons since we're controlling create permission with canCreate prop
                return defaultButtons;
            }}
        >
            <Stack spacing={3}>
                {isGuest && (
                    <Alert
                        severity="info"
                        icon={<Domain />}
                        sx={{
                            borderRadius: 2,
                            '& .MuiAlert-icon': {
                                color: theme.palette.info.main
                            }
                        }}
                    >
                        {translate('company.guest_view_only', 'Vous pouvez consulter les informations de votre organisation. Pour les modifier, contactez votre administrateur.')}
                    </Alert>
                )}

                {/* Only show search for ADMIN or MODERATOR without a company */}
                {(currentUser?.role === 'ADMIN' || (currentUser?.role === 'MODERATOR' && !currentUser?.entrepriseId)) && (
                    <SectionCard
                        title={translate('company.filter.title', 'Rechercher')}
                        icon={<Search />}
                    >
                        <TextField
                            label={translate('company.filter.search', 'Rechercher une organisation')}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            fullWidth
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                            placeholder={translate('company.filter.placeholder', 'Nom ou ID...')}
                        />
                    </SectionCard>
                )}

                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: 'hidden'
                    }}
                >
                    <DataGrid
                        {...dataGridProps}
                        rows={filteredData}
                        columns={columns}
                        autoHeight
                        rowHeight={70}
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-row': {
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                }
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.background.paper, 0.8)
                                    : theme.palette.grey[50],
                                borderBottom: `2px solid ${theme.palette.divider}`,
                            }
                        }}
                        components={{
                            NoRowsOverlay: () => (
                                <EmptyState
                                    icon={<Business sx={{ fontSize: 48 }} />}
                                    title={searchText
                                        ? translate('company.empty.search', 'Aucune organisation trouvée')
                                        : translate('company.empty.title', 'Aucune organisation')
                                    }
                                    description={searchText
                                        ? translate('company.empty.search_desc', 'Essayez avec d\'autres critères de recherche')
                                        : translate('company.empty.desc', 'Créez votre première organisation pour commencer')
                                    }
                                    action={
                                        !searchText && canCreateCompany
                                            ? {
                                                label: translate('company.create_button', 'Créer une organisation'),
                                                onClick: () => create('company')
                                            }
                                            : undefined
                                    }
                                />
                            ),
                        }}
                    />
                </Paper>
            </Stack>
        </List>
    );
};
